import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Icon, theme, TitleText, VStack, Button } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

// ────────────────────────────────────────────────────────────────────────────────

export const ReceiptClaimCtaBanner = (props: ReceiptClaimCtaBannerProps) => {
  const { isLoggedIn, isClaimed, receiptId } = props;

  const { track } = useTelemetry();
  const router = useRouter();

  // ─── Handlers ──────────────────────────────────────────────────

  const handleClaimPress = useCallback(() => {
    track('receipt.claim-cta.press', { isLoggedIn });

    if (isLoggedIn) {
      router.navigate(`/receipt/claim/${receiptId}`);
      return;
    }

    router.navigate({
      pathname: '/join-or-sign-in',
      params: { redirect: 'receipt-claim', redirectToReceiptId: receiptId },
    });
  }, [track, isLoggedIn, router, receiptId]);

  const handleLoyaltyRedirectPress = useCallback(() => {
    track('receipt.sg-rewards-cta.press');

    router.dismissTo('/sg-rewards');
  }, [track, router]);

  // ────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <VStack gap={5}>
        <View style={styles.infoTitleLine}>
          <TitleText sizeMatch={['18']} style={styles.title}>
            {isClaimed ? (
              <FormattedMessage {...messages.claimedTitle} />
            ) : (
              <FormattedMessage {...messages.unclaimedTitle} />
            )}
          </TitleText>
          <Icon name="IconSgRewardSweetCorn" width={24} height={24} />
        </View>

        <BodyText sizeMatch={['14']} style={styles.body}>
          <FormattedMessage {...getBodyMessage({ isClaimed, isLoggedIn })} />
        </BodyText>
      </VStack>

      <View style={styles.cta}>
        <Button onPress={isClaimed ? handleLoyaltyRedirectPress : handleClaimPress}>
          {isClaimed ? (
            <FormattedMessage {...messages.viewPointsCta} />
          ) : (
            <FormattedMessage {...messages.claimCta} />
          )}
        </Button>
      </View>
    </View>
  );
};

// ─── Helper ───────────────────────────────────────────────────────────────────

const getBodyMessage = ({ isClaimed, isLoggedIn }: { isClaimed: boolean; isLoggedIn: boolean }) => {
  if (isClaimed) return messages.claimedBody;
  if (isLoggedIn) return messages.loggedInBody;
  return messages.body;
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  unclaimedTitle: {
    defaultMessage: 'Earn points from this order',
    description: 'Receipt | Claim Order Banner | Unclaimed Title Variation',
  },
  claimedTitle: {
    defaultMessage: 'You were awarded points for this order!',
    description: 'Receipt | Claim Order Banner | Claimed Title Variation',
  },
  body: {
    defaultMessage: 'Join or sign in to unlock your points and get rewards',
    description: 'Receipt | Claim Order Banner | Logged Out Body',
  },
  loggedInBody: {
    defaultMessage: 'Claim this order to earn points towards rewards and free Sweetgreen',
    description: 'Receipt | Claim Order Banner | Logged In Body',
  },
  claimedBody: {
    defaultMessage: "You'll see them in your account within 24h",
    description: 'Receipt | Claim Order Banner | Claimed Order Body',
  },
  claimCta: {
    defaultMessage: 'Claim order',
    description: 'Receipt | Claim Order Banner | Claim Order CTA',
  },
  viewPointsCta: {
    defaultMessage: 'View points',
    description: 'Receipt | Claim Order Banner | View Points CTA',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.FOREST,
    borderRadius: theme.radius.large,
    padding: theme.spacing['4'],
  },
  infoTitleLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    color: theme.colors.SWEET_CORN,
  },
  body: {
    color: theme.colors.WHITE,
  },
  cta: {
    paddingTop: theme.spacing['4'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type ReceiptClaimCtaBannerProps = {
  isLoggedIn: boolean;
  isClaimed: boolean;
  receiptId: string;
};
